// Backup export/restore + Excel/PDF report export. Ported from the original app's
// RESTORE FROM BACKUP and EXPORT REPORT sections.
import * as XLSX from 'xlsx';
import { doc, getDoc } from 'firebase/firestore';
import { S, MEALS, MEAL_LABELS, ARCHIVE_PASSWORD, applyState } from '../state.js';
import { save } from '../lib/sync.js';
import { db, auth } from './firebase.js';
import { today, fmtDate, shiftDate } from './dates.js';
import { escHtml } from './format.js';
import { wkStatusLine } from './calc.js';
import { MEAS_TYPES } from '../pages/measurements.js';
import { openModal, closeModal } from './modal.js';
import { clearCache } from './storage.js';
import { goNav } from './router.js';
import { showToast } from '../components/toast.js';

export function doExport() {
  const blob = new Blob([JSON.stringify(S, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'level-up_backup_' + today() + '.json'; a.click();
  S.hasExported = true; save();
}

// ── Restore ──
let pendingRestoreData = null;

export function handleRestoreFile(inputEl) {
  const file = inputEl.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = e => {
    let data;
    try { data = JSON.parse(e.target.result); }
    catch (err) {
      document.getElementById('restorePreview').textContent = 'Файл повреждён или это не JSON — не похоже на бэкап LEVEL UP.';
      document.getElementById('restorePreview').style.color = 'var(--red)';
      openModal('shRestoreConfirm');
      document.querySelector('#shRestoreConfirm .btn-danger').style.display = 'none';
      inputEl.value = ''; return;
    }
    document.querySelector('#shRestoreConfirm .btn-danger').style.display = '';
    if (!data || typeof data !== 'object' || !('foodLog' in data) || !('weightLog' in data)) {
      document.getElementById('restorePreview').textContent = 'Файл не похож на бэкап LEVEL UP — нет привычной структуры данных.';
      document.getElementById('restorePreview').style.color = 'var(--red)';
      openModal('shRestoreConfirm');
      document.querySelector('#shRestoreConfirm .btn-danger').style.display = 'none';
      inputEl.value = '';
      return;
    }
    pendingRestoreData = data;
    const foodDays = Object.keys(data.foodLog || {}).length;
    const wLen = (data.weightLog || []).length;
    const wkLen = (data.workoutLog || []).length;
    document.getElementById('restorePreview').style.color = '';
    document.getElementById('restorePreview').textContent =
      `В файле: ${foodDays} дн. дневника питания, ${wLen} записей веса, ${wkLen} тренировок.`;
    document.getElementById('restorePassInp').value = '';
    inputEl.value = '';
    openModal('shRestoreConfirm');
  };
  reader.readAsText(file);
}

export function confirmRestore() {
  const passInp = document.getElementById('restorePassInp');
  if (passInp.value !== ARCHIVE_PASSWORD) { passInp.style.borderColor = 'var(--red)'; passInp.value = ''; passInp.placeholder = 'Неверный пароль'; return; }
  if (!pendingRestoreData) return;
  applyState(pendingRestoreData);
  pendingRestoreData = null;
  save(true);
  closeModal('shRestoreConfirm');
  goNav('home');
}

// ── Restore from a daily backup snapshot (see lib/sync.js maybeWriteDailyBackup) ──
// A separate, never-overwritten Firestore document per calendar day — the safety net for
// "a day's data went missing" beyond what live sync alone protects against.
let pendingBackupData = null;

export function openBackupRestore() {
  document.getElementById('backupDateInp').value = '';
  document.getElementById('backupPreview').textContent = '';
  document.getElementById('backupPassInp').value = '';
  pendingBackupData = null;
  openModal('shBackupRestore');
}

export async function findDailyBackup() {
  const date = document.getElementById('backupDateInp').value;
  const preview = document.getElementById('backupPreview');
  if (!date) { preview.textContent = 'Выбери дату'; preview.style.color = 'var(--red)'; return; }
  const uid = auth.currentUser?.uid;
  if (!uid) { preview.textContent = 'Нужно быть в сети и войти в аккаунт'; preview.style.color = 'var(--red)'; return; }
  preview.textContent = 'Ищу…'; preview.style.color = '';
  try {
    const snap = await getDoc(doc(db, 'userBackups', uid + '_' + date));
    if (!snap.exists() || !snap.data().data) {
      preview.textContent = 'За эту дату бэкапа нет — он появляется не раньше первого сохранения в тот день.';
      preview.style.color = 'var(--red)';
      pendingBackupData = null;
      return;
    }
    const data = JSON.parse(snap.data().data);
    pendingBackupData = data;
    const foodDays = Object.keys(data.foodLog || {}).length;
    const wLen = (data.weightLog || []).length;
    const wkLen = (data.workoutLog || []).length;
    preview.style.color = '';
    preview.textContent = `Бэкап найден: ${foodDays} дн. дневника питания, ${wLen} записей веса, ${wkLen} тренировок. Это заменит ВСЕ текущие данные содержимым этого бэкапа.`;
  } catch (e) {
    preview.textContent = 'Не удалось найти бэкап — ' + e.message;
    preview.style.color = 'var(--red)';
    pendingBackupData = null;
  }
}

export function confirmBackupRestore() {
  const passInp = document.getElementById('backupPassInp');
  if (passInp.value !== ARCHIVE_PASSWORD) { passInp.style.borderColor = 'var(--red)'; passInp.value = ''; passInp.placeholder = 'Неверный пароль'; return; }
  if (!pendingBackupData) return;
  applyState(pendingBackupData);
  pendingBackupData = null;
  save(true);
  closeModal('shBackupRestore');
  goNav('home');
}

// ── Excel / PDF export ──
// Any combination of types, over any date range (or all time if left blank).
let expTypes = new Set(['food']);
let expFormat = 'xlsx';
let expFrom = '';
let expTo = '';

export function toggleExpType(t, btn) {
  if (expTypes.has(t)) {
    if (expTypes.size === 1) return; // keep at least one type selected
    expTypes.delete(t);
    btn.classList.remove('on');
  } else {
    expTypes.add(t);
    btn.classList.add('on');
  }
}
export function setExpFormat(f, btn) {
  expFormat = f;
  document.querySelectorAll('#expFormatGroup .toggle-btn').forEach(b => b.classList.remove('on'));
  btn.classList.add('on');
}

export function onExpRangeChange() {
  expFrom = document.getElementById('expFrom').value;
  expTo = document.getElementById('expTo').value;
}

// 0 = "Всё время" (clears the range, no filtering at all).
export function setExpQuickRange(days) {
  if (days === 0) {
    expFrom = ''; expTo = '';
  } else {
    expTo = today();
    expFrom = shiftDate(expTo, -(days - 1));
  }
  document.getElementById('expFrom').value = expFrom;
  document.getElementById('expTo').value = expTo;
}

export function openExportModal() {
  expTypes = new Set(['food']);
  expFormat = 'xlsx';
  document.querySelectorAll('#expTypeGroup .toggle-btn').forEach(b => b.classList.toggle('on', expTypes.has(b.dataset.type)));
  document.querySelectorAll('#expFormatGroup .toggle-btn').forEach((b, i) => b.classList.toggle('on', i === 0));
  setExpQuickRange(7); // sensible default: last 7 days
  openModal('shExport');
}

function inExportRange(dateStr) {
  if (expFrom && dateStr < expFrom) return false;
  if (expTo && dateStr > expTo) return false;
  return true;
}

function buildFoodRows() {
  const rows = [['Дата', 'Приём', 'Блюдо', 'Граммы', 'Ккал', 'Белки', 'Жиры', 'Углеводы', 'Съедено']];
  Object.keys(S.foodLog).sort().forEach(d => {
    if (!inExportRange(d)) return;
    const fl = S.foodLog[d];
    MEALS.forEach(m => {
      (fl[m] || []).forEach(it => {
        rows.push([fmtDate(d), MEAL_LABELS[m], it.name, it.g || 0, it.k || 0, it.p || 0, it.f || 0, it.c || 0, it.eaten === false ? 'Нет' : 'Да']);
      });
    });
  });
  return rows;
}

function buildWorkoutRows() {
  const rows = [['Дата', 'Тип', 'Ккал', 'Шаги', 'Минуты', 'Заметка']];
  [...S.workoutLog].sort((a, b) => a.date.localeCompare(b.date)).forEach(w => {
    if (!inExportRange(w.date)) return;
    const st = wkStatusLine(w);
    rows.push([fmtDate(w.date), st.text, w.kcalBurned || 0, w.steps || 0, w.duration || 0, w.note || '']);
  });
  return rows;
}

function buildWeightRows() {
  const rows = [['Дата', 'Вес, кг']];
  [...S.weightLog].sort((a, b) => a.date.localeCompare(b.date)).forEach(w => {
    if (!inExportRange(w.date)) return;
    rows.push([fmtDate(w.date), w.w]);
  });
  return rows;
}

function buildMeasurementRows() {
  const rows = [['Дата', 'Замер', 'Значение', 'Ед.']];
  const meas = S.measurements || {};
  Object.keys(MEAS_TYPES).forEach(type => {
    const unit = type === 'fat' ? '%' : 'см';
    [...(meas[type] || [])].sort((a, b) => a.date.localeCompare(b.date)).forEach(m => {
      if (!inExportRange(m.date)) return;
      rows.push([fmtDate(m.date), MEAS_TYPES[type], m.value, unit]);
    });
  });
  return rows;
}

const EXP_SHEETS = {
  food: ['Питание', buildFoodRows],
  workout: ['Тренировки', buildWorkoutRows],
  weight: ['Вес', buildWeightRows],
  measurements: ['Замеры', buildMeasurementRows],
};

export function runExport() {
  if (expFormat === 'xlsx') exportExcel();
  else exportPrintReport();
  closeModal('shExport');
}

function exportExcel() {
  const wb = XLSX.utils.book_new();
  Object.keys(EXP_SHEETS).forEach(type => {
    if (!expTypes.has(type)) return;
    const [name, build] = EXP_SHEETS[type];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(build()), name);
  });
  const range = expFrom || expTo ? `${expFrom || '...'}_${expTo || '...'}` : 'all';
  XLSX.writeFile(wb, 'LEVEL_UP_' + range + '.xlsx');
  S.hasExported = true; save();
}

function exportPrintReport() {
  let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>LEVEL UP — экспорт</title>
    <style>
      body{font-family:-apple-system,Arial,sans-serif;padding:24px;color:#1A1918}
      h1{font-size:20px;margin-bottom:2px} .sub{color:#777;font-size:12px;margin-bottom:20px}
      h2{font-size:15px;margin-top:26px;margin-bottom:6px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
      th{background:#f0f0ee}
      @media print{ h2{page-break-after:avoid} tr{page-break-inside:avoid} }
    </style></head><body>
    <h1>LEVEL UP — экспорт данных</h1>
    <div class="sub">Сформировано: ${fmtDate(today())}${expFrom || expTo ? ` · период: ${expFrom ? fmtDate(expFrom) : '…'} — ${expTo ? fmtDate(expTo) : '…'}` : ' · всё время'}</div>`;

  function addTableHtml(title, rows) {
    html += `<h2>${title}</h2><table><thead><tr>${rows[0].map(h => `<th>${escHtml(String(h))}</th>`).join('')}</tr></thead><tbody>`;
    rows.slice(1).forEach(r => { html += `<tr>${r.map(c => `<td>${escHtml(String(c))}</td>`).join('')}</tr>`; });
    html += `</tbody></table>`;
  }
  Object.keys(EXP_SHEETS).forEach(type => {
    if (!expTypes.has(type)) return;
    const [name, build] = EXP_SHEETS[type];
    addTableHtml(name, build());
  });
  html += `<script>window.onload=()=>{window.print();}<\/script></body></html>`;

  const w = window.open('', '_blank');
  if (!w) { showToast('Разреши всплывающие окна для этого сайта, чтобы сформировать отчёт'); return; }
  w.document.write(html);
  w.document.close();
}

export function resetAll() {
  clearCache();
  location.reload();
}
